'use client';

import { ErrorMessage } from '@hookform/error-message';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FiCheck } from 'react-icons/fi';

import { Button } from '@/component/parts/button/Button/Button';
import { InputGroup } from '@/component/parts/form/InputGroup/InputGroup';
import { InputGroupHelperMessage } from '@/component/parts/form/InputGroup/InputGroupHelperMessage';
import { TextInput } from '@/component/parts/form/TextInput/TextInput';
import { Column } from '@/component/parts/layout/Column/Column';
import { Label } from '@/component/parts/typography/Label/Label';

import { startEmailVerification } from './startEmailVerification';

interface FormValues {
  email: string;
}

class EmailVerificationFormError extends Error {}

export const EmailVerificationForm: React.FC<{
  slug: string;
  organizationId: string;
  entryFormId: string;
}> = ({ slug, organizationId, entryFormId }) => {
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>();

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const r = await startEmailVerification({
        organizationId,
        entryFormId,
        email: values.email.trim(),
      });
      if (r.success) {
        router.push(`/e/${slug}/email-verification/sent`);
      } else {
        throw new EmailVerificationFormError();
      }
    },
    onError: (error: Error) => {
      setError('email', {
        type: 'custom',
        message:
          error instanceof EmailVerificationFormError
            ? '正しいメールアドレスを入力してください。'
            : 'システムエラーが発生しました。時間を空けて再度お試しください。',
      });
    },
  });

  return (
    <form onSubmit={handleSubmit((values) => mutation.mutate(values))}>
      <Column spacing="10px">
        <InputGroup>
          <Label>メールアドレス</Label>
          <TextInput
            type="email"
            fill
            {...register('email', { required: true, maxLength: 255 })}
            maxLength={255}
            placeholder="入力してください"
          />
          <InputGroupHelperMessage variant="error">
            <ErrorMessage
              errors={errors}
              name="email"
              message={errors.email?.message || 'メールアドレスを入力してください。'}
            />
          </InputGroupHelperMessage>
          <InputGroupHelperMessage>
            ※&nbsp;noreply@sumofuri.com、sd@linkage-inc.co.jpからのメールを受信できるように設定してください
          </InputGroupHelperMessage>
        </InputGroup>
        <Button
          type="submit"
          variant="default"
          disabled={mutation.isPending || mutation.isSuccess}
          leftIcon={<FiCheck />}
        >
          登録する
        </Button>
      </Column>
    </form>
  );
};
